import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getStoredUser, getUserToken, isTrue, themeCssVars } from "../../functions/themeAssets";
import "../common/player.css";

/** Score, time and the thank-you message the server picked for that score. */
const CONFETTI_TONES = ["var(--iq-yes)", "var(--iq-maybe)", "var(--iq-no)", "var(--iq-box)"];
const CONFETTI = Array.from({ length: 12 }, (_, i) => i);

export default function ThankYou() {
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const [report, setReport] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const user = getStoredUser();
  const leaderboardEnabled = isTrue(themeData?.leaderboard_toggle);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/fetchReport`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getUserToken(user)}` },
      body: JSON.stringify({ userId: user.userId }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json.success && json.report) setReport(json.report);
        else navigate("/rules", { replace: true }); // nothing played yet
      })
      .catch(() => {
        if (!cancelled) navigate("/rules", { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !leaderboardEnabled) return undefined;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/getGameLeaderboard`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getUserToken(user)}` },
      body: JSON.stringify({ userId: user.userId }),
    })
      .then((r) => r.json())
      .then((json) => {
        if (cancelled) return;
        if (json.success) setLeaderboard(json.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboardEnabled]);

  if (!report) {
    return (
      <div className="iq-loader">
        <div className="iq-loader-spinner" />
      </div>
    );
  }

  return (
    <div className={`iq-thanks${leaderboardEnabled ? " iq-thanks--lb" : ""}`} style={themeCssVars(themeData)}>
      <div className="iq-thanks-badge" aria-hidden="true">
        {CONFETTI.map((i) => (
          <span
            key={i}
            className="iq-confetti"
            style={{ "--iq-angle": `${i * 30}deg`, "--iq-tone": CONFETTI_TONES[i % CONFETTI_TONES.length] }}
          />
        ))}
        <span className="iq-thanks-star" role="img" aria-label="star">
          ⭐
        </span>
      </div>
       {report.message && <h3 className=" iq-thanks-title">{report.message}</h3>}

      {leaderboardEnabled ? (
        <div className="iq-leaderboard">
          <div className="iq-lb-header">
            <span className="iq-lb-col iq-lb-col-rank">Rank</span>
            <span className="iq-lb-col iq-lb-col-name">Name</span>
            <span className="iq-lb-col iq-lb-col-points">Points</span>
            <span className="iq-lb-col iq-lb-col-time">Time</span>
          </div>

          {!leaderboard ? (
            <div className="iq-lb-empty">Loading…</div>
          ) : leaderboard.top.length === 0 ? (
            <div className="iq-lb-empty">No entries yet.</div>
          ) : (
            <div className="iq-lb-rows">
              {[...leaderboard.top, ...(leaderboard.me ? [leaderboard.me] : [])].map((r) => {
                const isMe = String(r.userId) === String(user.userId);
                return (
                  <div key={r.rank} className={`iq-lb-row ${isMe ? "iq-lb-row-me" : ""}`}>
                    <span className="iq-lb-col iq-lb-col-rank">
                      <span className="iq-lb-rank-icon iq-lb-rank-plain">{r.rank}</span>
                    </span>
                    <span className="iq-lb-col iq-lb-col-name">
                      <span className="iq-lb-name-text">{r.name}</span>
                    </span>
                    <span className="iq-lb-col iq-lb-col-points">{r.points}</span>
                    <span className="iq-lb-col iq-lb-col-time">{r.time}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <h4 className="iq-thanks-score">
          Your score is <strong>{report.points}</strong> in <strong>{report.time}</strong> minutes.
        </h4>
      )}
    </div>
  );
}
